import {
  DataUnavailableWrapper,
  StyledDataUnavailableLight,
} from '@/chart/components/all-charts-styles';
import { datasetTypeLabels, datasetTypes } from '@/constants';
import { propertyDetailsCalendarMgr } from '@/utils/listings-data-utils';
import moment from 'moment';
import 'rc-calendar/assets/index.css';
import { useCallback, useEffect, useState } from 'react';
import { DetailsVisHeader } from '../details-vis-header';
import { CalendarControls } from './calendar-controls';
import { getCalendarDayData, momentKeyFromDate } from './calendar-utils';
import { CustomDateCell } from './custom-date-cell';
import {
  CalendarWrapper,
  EstimatedRentalActivitiesWrapper,
  Legend,
  StyledRangeCalendar,
} from './details-calendar-styles';

const MIN_MONTH_INDEX = 1;
const MONTHS_TO_SCROLL = 1;
const MONTHS_BEYOND_CURRENT = 4;

/**
 * Initializes container HTML element for the calendar controls by inserting them in DOM
 *
 * @param {*} cb callback for setting state in component calling this
 */
function initializeControlsContainer(cb) {
  const leftHeader = document.querySelector(
    '.rc-calendar-range-left .rc-calendar-header > div'
  );
  const rightHeader = document.querySelector(
    '.rc-calendar-range-right .rc-calendar-header > div'
  );
  const leftBtnContainer = document.createElement('div');
  leftBtnContainer.id = 'left-change-month-btn-container';
  rightHeader.id = 'right-change-month-btn-container';
  if (leftHeader) {
    // ensuring there's a parent element before trying to add a child to avoid error
    leftHeader.prepend(leftBtnContainer);
  }
  cb({ left: leftBtnContainer, right: rightHeader });
}

/**
 * Utility function for determining the if there is calendar data
 * @param {*} data passed as propertyData.dataPoints
 */
function hasCalendarActivity(data) {
  if (!data) return false; // If there are no data points
  // If there are data points, make sure there is some calendarActivity
  return data.some((set) => set.calendarActivity);
}

/**
 * Renders calendar.
 * Two months.
 * Displays ADR, booked/blocked
 *
 * @param {*} props.propertyData
 */
export function EstimatedRentalActivitiesCalendar({ propertyData }) {
  // This component assumes that dataPoints are available. Parent component must handle it missing
  const { dataPoints } = propertyData;

  // Placeholder for html containers for control buttons. { left, right }
  const [controlsHtmlContainers, setControlsHtmlContainers] = useState({});

  // The current (calendar left side) month. Updating this will change months viewed in calendar
  const [currentMonthIndex, setCurrentMonthIndex] = useState(
    dataPoints.length - MONTHS_BEYOND_CURRENT + 1
  );

  // To update currentMonthIndex with the proper value when switching properties
  useEffect(() => {
    setCurrentMonthIndex(dataPoints.length - MONTHS_BEYOND_CURRENT + 1);
  }, [dataPoints]);

  useEffect(() => {
    initializeControlsContainer(setControlsHtmlContainers);
  }, []);

  // TODO: Put in useEffect
  const dataPointsByMomentDict = {};
  dataPoints.forEach((dataPoint) => {
    const dateKey = momentKeyFromDate(dataPoint.date);
    dataPointsByMomentDict[dateKey] = dataPoint;
  });

  /**
   * For now, must have more than one dataPoint for showing the left side
   * Have a fallback for calendar data retrieval as it is possible to attempt
   * to access beyond what is allocated in the dataPoints array when
   * switching properties via the search function
   * Use dataPoints.length - 2, as the 2nd latest month is shown on the left side of the calendar
   * Use dataPoints.length - 1, as the latest month is shown on the right side of the calendar
   */
  // TODO: talk to Bing. She has to decide on what to show when there's only one month dataPoint
  const leftDate =
    dataPoints.length > 1 &&
    (dataPoints[currentMonthIndex - 1]?.date ||
      dataPoints[dataPoints.length - 2].date);
  const rightDate =
    dataPoints[currentMonthIndex]?.date ||
    dataPoints[dataPoints.length - 1].date;

  /* Determines months viewed in calendar */
  const calendarRanges = [
    moment(leftDate).startOf('month'), // left side
    moment(rightDate).startOf('month'), // right side
  ];

  const maxMonthIdx = dataPoints.length - 1; // maximum allowed index for current month

  const { colorGetter } = propertyDetailsCalendarMgr;

  const showLeftButton =
    controlsHtmlContainers.left && !(currentMonthIndex <= MIN_MONTH_INDEX);
  const showRightButton =
    controlsHtmlContainers.right && !(currentMonthIndex >= maxMonthIdx);

  const scrollCalLeft = useCallback(() => {
    // Check if the button is displayed, then scroll the calendar
    if (showLeftButton) {
      /**
       * Check if we're scrolling beyond the left limit of the calendar:
       *    If yes, set current month index to the safest minimum index
       *    If no, set current month index to minus 1 of the current month index
       */
      setCurrentMonthIndex((prevMonthIndex) =>
        prevMonthIndex - MONTHS_TO_SCROLL <= MIN_MONTH_INDEX
          ? MIN_MONTH_INDEX
          : prevMonthIndex - MONTHS_TO_SCROLL
      );
    }
  }, [showLeftButton]);

  const scrollCalRight = useCallback(() => {
    // Check if the button is displayed, then scroll the calendar
    if (showRightButton) {
      /**
       * Check if we're scrolling beyond the right limit of the calendar:
       *    If yes, set current month index to the safest maximum index
       *    If no, set current month index to plus 1 of the current month index
       */
      setCurrentMonthIndex((prevMonthIndex) =>
        prevMonthIndex + MONTHS_TO_SCROLL >= dataPoints.length
          ? dataPoints.length - 1
          : prevMonthIndex + MONTHS_TO_SCROLL
      );
    }
  }, [showRightButton, dataPoints]);

  // If there is no calendar data, display no data placeholder
  if (!hasCalendarActivity(dataPoints)) {
    return (
      <EstimatedRentalActivitiesWrapper>
        <DataUnavailableWrapper>
          <StyledDataUnavailableLight />
        </DataUnavailableWrapper>
      </EstimatedRentalActivitiesWrapper>
    );
  }

  return (
    <div>
      <DetailsVisHeader title={datasetTypeLabels[datasetTypes.OCCUPANCY]} />

      <CalendarControls
        controlsHtmlContainers={controlsHtmlContainers}
        leftOnClick={scrollCalLeft}
        rightOnClick={scrollCalRight}
        showLeftButton={showLeftButton}
        showRightButton={showRightButton}
      />

      <EstimatedRentalActivitiesWrapper>
        <CalendarWrapper>
          <StyledRangeCalendar
            renderSidebar={() => <Legend />}
            showToday={false}
            showDateInput={false}
            value={calendarRanges} // set the fixed 2 months in calendar
            selectedValue={[]} // disable select when we give it an empty array
            defaultSelectedValue={[]}
            dateRender={(currentMoment) => {
              if (!currentMoment.isValid()) return <div />;
              const dateData = getCalendarDayData(
                currentMoment,
                dataPointsByMomentDict
              );
              return (
                <div onClick={(e) => e.stopPropagation()}>
                  <CustomDateCell
                    dateData={dateData}
                    colorGetter={colorGetter}
                  />
                </div>
              );
            }}
            mode={['date', 'date']} // disable the click action when clicking the month and year in the header
            seperator={null} // the ~ sign between the 2 months in the header
            hoverValue={[]} // disable the background color when hovering on date cells
          />
        </CalendarWrapper>
      </EstimatedRentalActivitiesWrapper>
    </div>
  );
}
